import type * as React from "react";
import { cn } from "../../lib/utils.ts";

export interface BottomNavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  badge?: number;
  onSelect?: () => void;
  /** Render as a link when an href is provided (e.g. wrap with router Link via asChild upstream). */
  href?: string;
}

export interface BottomNavProps {
  items: BottomNavItem[];
  className?: string;
}

/**
 * Fixed mobile tab bar. Opaque dark base (so bright content can't bloom through
 * the blur in a dark bar), a gold pill on the active tab, large tap targets.
 */
export function BottomNav({ items, className }: BottomNavProps) {
  return (
    <nav
      className={cn(
        "fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-[calc(28rem-1.5rem)] items-stretch gap-1 rounded-2xl border border-hairline-strong p-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))]",
        "bg-[linear-gradient(180deg,oklch(23%_0.016_55_/_98%),oklch(15%_0.012_58_/_99%))] shadow-[var(--shadow-pop)] backdrop-blur-md",
        className,
      )}
      aria-label="Primary"
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={item.onSelect}
          aria-current={item.active ? "page" : undefined}
          className={cn(
            "relative flex min-h-[2.9rem] flex-1 flex-col items-center justify-center gap-1 rounded-[1.125rem] px-1 py-1.5 font-medium text-[0.62rem] transition-colors [&_svg]:size-[1.35rem] [&_svg]:transition-transform",
            item.active
              ? "bg-[linear-gradient(180deg,oklch(79%_0.12_80_/_18%),oklch(79%_0.12_80_/_6%))] text-gold shadow-[inset_0_0_0_1px_oklch(79%_0.12_80_/_30%)] [&_svg]:-translate-y-px [&_svg]:scale-105"
              : "text-cream-2 hover:text-cream",
          )}
        >
          <span className="relative">
            {item.icon}
            {item.badge ? (
              <span className="-right-2.5 -top-1.5 absolute grid h-[1.05rem] min-w-[1.05rem] place-items-center rounded-full bg-lime px-1 font-bold text-[0.6rem] text-[oklch(26%_0.06_120)] shadow-[0_0_0_2px_oklch(15%_0.012_58)]">
                {item.badge > 9 ? "9+" : item.badge}
              </span>
            ) : null}
          </span>
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
